import { TEST_IDS } from '@va/constants';
import { CheckmarkIcon, CopyIcon } from '@va/icons';
import { useTranslate } from '@va/localization';
import { useCopyToClipboard } from '@va/util/hooks';
import classNames from 'classnames';
import React from 'react';
import { Button, ButtonColors } from '../button';
import { Paragraph, ParagraphWithTooltip, fontWeights, paragraphSizes } from '../typography';

type CopyableSectionProps = {
  wrapperClassName?: string;
  info?: string;
  label: String;
  value: string;
  icon?: React.ReactNode;
  className?: string;
  copyBtnOptions?: {
    disabled?: boolean;
    type?: 'submit' | 'button';
    text?: string;
    color?: ButtonColors;
    hidden?: boolean;
    onClick?: () => void;
    onHover?: () => void;
  };
  size?: 'small' | 'large';
  required?: boolean;
};

export const CopyableSection: React.FC<CopyableSectionProps> = ({
  wrapperClassName,
  value,
  label,
  info,
  icon,
  className,
  copyBtnOptions,
  size = 'large',
  required,
}) => {
  const translate = useTranslate();

  const { onClickCopy: copyUrl, success: copyToClipboardSuccess } = useCopyToClipboard();

  return (
    <div className={classNames(wrapperClassName)}>
      <div className={classNames('flex items-center bg-white-snow gap-2 rounded-t-15 rounded-b-15 p-6px', className)}>
        {icon && (
          <div
            className={classNames('shrink-0 flex justify-center items-center', {
              'w-12 h-12': size === 'large',
              'w-9 h-9': size === 'small',
            })}
          >
            {icon}
          </div>
        )}
        <div className='ml-2 grow overflow-hidden'>
          <Paragraph colorClassName='text-gray-dark-charchoal' size={paragraphSizes.tiny} weight={fontWeights.medium}>
            {label}
            {required && <span className='text-red-negative ml-1'>*</span>}
          </Paragraph>

          <ParagraphWithTooltip
            colorClassName='text-gray-dark-charchoal'
            weight={fontWeights.medium}
            tooltipProps={{ interactive: true }}
          >
            {value}
          </ParagraphWithTooltip>
        </div>
        {!copyBtnOptions?.hidden && (
          <Button
            onClick={() => {
              if (!copyBtnOptions?.disabled) copyUrl(value);
              copyBtnOptions?.onClick?.();
            }}
            onHover={copyBtnOptions?.onHover}
            tooltip={
              copyToClipboardSuccess ? translate('button.copiedToClipboard') : translate('button.copyToClipboardText')
            }
            color={copyBtnOptions?.color ?? 'quaternary'}
            icon={() =>
              copyToClipboardSuccess ? (
                <CheckmarkIcon
                  color={'white'}
                  className={classNames('shrink-0', {
                    'w-[0.9375rem] h-[0.9375rem]': size === 'small',
                    'w-[1.125rem] h-[1.125rem]': size === 'large',
                  })}
                />
              ) : (
                <CopyIcon
                  color={'#3C3C3C'}
                  className={classNames('shrink-0', {
                    'w-[0.9375rem] h-[0.9375rem]': size === 'small',
                    'w-[1.125rem] h-[1.125rem]': size === 'large',
                  })}
                />
              )
            }
            className={classNames(copyToClipboardSuccess && '!bg-green-pure !text-white')}
            type={copyBtnOptions?.type}
            text={copyBtnOptions?.text}
            size={size}
            data-testid={TEST_IDS.generic.buttons.copyToClipboard}
          />
        )}
      </div>
      {info && (
        <Paragraph className='ml-3' size={paragraphSizes.tiny}>
          {info}
        </Paragraph>
      )}
    </div>
  );
};
